import { forwardRef, Fragment, useImperativeHandle, useRef, useState } from 'react';
import { isNullish } from '@sas/nova-core/typeguards';
import cx from 'classnames';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { Button, ButtonVariant } from '../Button';
import { Icon } from '../Icon';
import { Flow } from '../Layouts/Flow';
import { Link } from '../Link';
import { Popover } from '../Popover/Popover';
import { Text } from '../Text';
import { getIconAndLabelTextFromType } from './__internal__/getIconAndLabelTextFromType';
import bundle from './NovaComponents-ObjectMarker-gui-icu.properties';
import cn from './ObjectMarker.css.json';
import './ObjectMarker.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useHtmlId = createUseHtmlId('ObjectMarker');
export const useObjectMarkerLocalizer = createUseTextLocalizer(bundle);
export const ObjectMarker = /*#__PURE__*/forwardRef(function ObjectMarker({
  variant = '',
  disabled = false,
  focusable = true,
  hideIcon = false,
  hideLabel = false,
  ...props
}, forwardedRef) {
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const [popperIsOpen, setPopperIsOpen] = useState(false);
  const localize = useObjectMarkerLocalizer();
  const {
    labelText: labelFromType,
    icon
  } = getIconAndLabelTextFromType(props.type, localize);
  const hasCustomLabel = !isNullish(props.text);
  const labelText = props.text ?? labelFromType;
  const hasPopover = !!(props.popperText || props.popoverProps) && !hideLabel;
  const dt = useDesignTokens();
  const tokens = {
    ...renderInlineCssVars({
      'spacing-xxs': dt.tokens['space-base-xxs'],
      'spacing-xs': dt.tokens['space-base-xs'],
      'icon-fg': dt.tokens['color-body-icon']
    }, 'object-marker')
  };
  const [iconId, labelId] = useHtmlId(2);
  useImperativeHandle(props.popperRef, () => ({
    setPopperIsOpen: value => {
      setPopperIsOpen(value);
    }
  }));
  let labelComponent = null;
  if (!hideLabel) {
    labelComponent = hasPopover ? /*#__PURE__*/_jsx(Link, {
      id: labelId,
      tabIndex: -1,
      wrapText: false,
      disabled: disabled,
      children: labelText
    }) : /*#__PURE__*/_jsx(Text, {
      id: labelId,
      disabled: disabled,
      wrapText: false,
      children: labelText
    });
  }
  let iconTooltip = '';
  {
    // an explicit icon tooltip takes first precedence
    if (!isNullish(props.iconTooltip)) iconTooltip = props.iconTooltip;
    // when there is anything custom about the label, whether by text or visibility,
    // the icon tooltip should be the default text associated with the icon
    else if (hasCustomLabel || hideLabel) iconTooltip = labelFromType;
    // otherwise, the tooltip will be suppressed (leaving it as '')
  }

  let iconAriaLabel;
  {
    // an explicit icon aria-label takes first precedence
    if (!isNullish(props.iconAriaLabel)) iconAriaLabel = props.iconAriaLabel;
    // when it's been determined that a tooltip should display on the icon,
    // (meaning Icon's "tooltip" prop will be populated with a value),
    // omit populating the aria-label, leaving it to be internally handled by Icon
    else if (iconTooltip !== '') iconAriaLabel = undefined;
    // otherwise, use an aria-label that matches the default text associated with
    // the Icon (ignore any custom label (props.text))
    else iconAriaLabel = labelFromType;
  }

  // determine what the ObjectMarker root aria-labelledby value should be.
  // will either point to the icon, label, or both
  const ariaLabelledByIds = [];
  if (!hideIcon && (iconTooltip === '' && hasCustomLabel || iconTooltip !== '' || !isNullish(props.iconAriaLabel))) ariaLabelledByIds.push(iconId);
  if (!hideLabel) ariaLabelledByIds.push(labelId);
  if (props.ariaLabelledBy) ariaLabelledByIds.push(props.ariaLabelledBy);
  const ariaLabelledBy = ariaLabelledByIds.join(' ');
  let iconAriaHidden = true;
  {
    // explicitly setting iconAriaHidden to false takes first precedence
    if (props.iconAriaHidden === false) iconAriaHidden = false;
    // when both iconTooltip and iconAriaLabel is "suppressed", iconAriaHidden should be true
    else if (props.iconTooltip === '' && iconAriaLabel === '') iconAriaHidden = true;
    // when the consumer has set any prop to a value that would affect
    // the icon's aria information, the icon should not be aria-hidden
    else if (!isNullish(props.text) || !isNullish(props.iconAriaLabel) || !isNullish(props.iconTooltip)) iconAriaHidden = false;
    // static OM with icon only should have iconAriaHidden should be false
    else if (!hasPopover && hideLabel) iconAriaHidden = false;
  }
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [hasPopover ? /*#__PURE__*/_jsx(Button, {
      id: props.id,
      variant: ButtonVariant.Light,
      tokens: () => ({
        bg: 'transparent',
        filter: 'none'
      }),
      style: {
        textDecoration: 'underline',
        border: 0,
        padding: 0,
        paddingInlineEnd: '3px',
        ...tokens,
        ...props.style
      },
      theme: () => {
        if (!disabled) {
          return {
            '--button-spacing-xs': '0',
            '--button-fg': dt.tokens['color-action-link-normal'],
            '--button-hover-fg': dt.tokens['color-action-link-hover'],
            '--button-active-fg': dt.tokens['color-action-link-active']
          };
        } else {
          return {
            '--button-spacing-xs': '0'
          };
        }
      },
      ref: div => {
        buttonRef.current = div;
        setRef(forwardedRef, div);
      },
      ariaLabel: props.ariaLabel,
      ariaLabelledBy: props.ariaLabelledBy,
      ariaDescribedBy: props.ariaDescribedBy,
      "aria-disabled": disabled,
      className: cx(cn.objectMarker, hasPopover && cn.objectMarkerLink, disabled && cn.disabled, props.className),
      onPress: () => {
        setPopperIsOpen(true);
      },
      focusable: focusable,
      disabled: disabled,
      testId: props.testId,
      text: !hideLabel ? labelText : undefined,
      icon: !hideIcon && icon ? {
        id: iconId,
        className: cx(props.type === 'AnimatedInProgress' ? cn.animatedInProgress : null, cn.icon),
        style: {
          marginInlineEnd: 2,
          ['--icon-neutral-override-fg']: dt.themeVariant === 'high-contrast' && !disabled ? dt.tokens['color-status-icon-neutral'] : null,
          ['--icon-alert-override-fg']: dt.themeVariant === 'high-contrast' && !disabled ? dt.tokens['color-status-icon-alert'] : null,
          ['--icon-warning-override-fg']: dt.themeVariant === 'high-contrast' && !disabled ? dt.tokens['color-status-icon-warning'] : null,
          ['--icon-success-override-fg']: dt.themeVariant === 'high-contrast' && !disabled ? dt.tokens['color-status-icon-success'] : null,
          ['--icon-information-override-fg']: dt.themeVariant === 'high-contrast' && !disabled ? dt.tokens['color-status-icon-info'] : null
        },
        icon,
        tokens: () => {
          if (props.type === 'Higher' || props.type === 'Lower' || props.type === 'SignificantlyHigher' || props.type === 'SignificantlyLower' || props.type === 'NegativeSentimentalLevel' || props.type === 'NeutralSentimentalLevel' || props.type === 'PositiveSentimentalLevel' || props.type === 'TrendDownStatus' || props.type === 'TrendNoChangeStatus' || props.type === 'TrendUpStatus') {
            if (variant === 'positive' && !disabled) {
              return {
                fg: dt.tokens['color-status-icon-success']
              };
            } else if (variant === 'negative' && !disabled) {
              return {
                fg: dt.tokens['color-status-icon-alert']
              };
            } else if (variant === 'flat' && !disabled) {
              return {
                fg: dt.tokens['color-status-icon-warning']
              };
            } else {
              return {};
            }
          } else {
            return {};
          }
        }
      } : undefined
    }) : /*#__PURE__*/_jsxs(Flow, {
      id: props.id,
      spacing: "xxs",
      style: {
        ...tokens,
        ...props.style,
        paddingInlineEnd: '3px'
      },
      ref: div => {
        rootRef.current = div;
        setRef(forwardedRef, div);
      },
      "aria-label": props.ariaLabel,
      "aria-labelledby": hasPopover ? ariaLabelledBy : props.ariaLabelledBy,
      "aria-describedby": props.ariaDescribedBy,
      "aria-disabled": disabled,
      className: cx(cn.objectMarker, hasPopover && cn.objectMarkerLink, disabled && cn.disabled, props.className),
      tabIndex: focusable ? 0 : -1,
      role: !hideLabel && props['data-with-labels'] ? 'textbox' : undefined,
      "aria-readonly": !hideLabel && props['data-with-labels'] ? true : undefined,
      onClick: event => {
        if (!disabled && hasPopover) {
          event.preventDefault();
          setPopperIsOpen(true);
        }
      },
      onKeyDown: event => (event.key === 'Enter' || event.key === ' ') && disabled === false && hasPopover && setPopperIsOpen(true),
      "data-testid": props.testId,
      children: [!hideIcon && icon ? /*#__PURE__*/_jsx(Icon, {
        id: iconId,
        className: cx(props.type === 'AnimatedInProgress' ? cn.animatedInProgress : null, cn.icon),
        ariaHidden: iconAriaHidden,
        ariaLabel: iconAriaLabel,
        tooltip: iconTooltip,
        icon: icon,
        disabled: disabled,
        tokens: () => {
          if (props.type === 'Higher' || props.type === 'Lower' || props.type === 'SignificantlyHigher' || props.type === 'SignificantlyLower' || props.type === 'NegativeSentimentalLevel' || props.type === 'NeutralSentimentalLevel' || props.type === 'PositiveSentimentalLevel' || props.type === 'TrendDownStatus' || props.type === 'TrendNoChangeStatus' || props.type === 'TrendUpStatus') {
            if (variant === 'positive') {
              return {
                fg: dt.tokens['color-status-icon-success']
              };
            } else if (variant === 'negative') {
              return {
                fg: dt.tokens['color-status-icon-alert']
              };
            } else if (variant === 'flat') {
              return {
                fg: dt.tokens['color-status-icon-warning']
              };
            } else {
              return {};
            }
          } else {
            return {};
          }
        }
      }) : null, labelComponent]
    }), /*#__PURE__*/_jsx(Popover, {
      isOpen: popperIsOpen,
      referenceElementRef: hasPopover ? buttonRef : rootRef,
      onClose: () => setPopperIsOpen(false),
      ariaLabel: localize('popover.information.label'),
      focusable: true,
      role: "dialog",
      style: {
        maxWidth: '24rem',
        maxHeight: '50vh'
      },
      children: props.popperText ?? '',
      ...props.popoverProps
    })]
  });
});
//# sourceMappingURL=ObjectMarker.js.map